import { skipToken } from "@reduxjs/toolkit/query";
import { TeammateType } from "@repo/app-types/entities/teammate";
import { asGradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import { Size } from "@repo/ui/helpers/helpers";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@repo/ui/toggleButton/segmentedControl";
import {
  IconFileText,
  IconLayoutSidebarRightExpand,
  IconPlayerPlay,
} from "@tabler/icons-react";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
} from "react";
import { Button as AriaButton, VisuallyHidden } from "react-aria-components";
import { useLocation, useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  usePlaybookGradingCallOutlineQuery,
  usePlaybookGradingQuery,
} from "../../api/entities/performancePlaybook.js";
import { useRecordingToolActivitiesQuery } from "../../api/entities/recording.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { AudioPlayerContext } from "../../components/audioPlayer/context.js";
import { TranscriptScrollPane } from "../../components/transcript/transcriptScrollPane.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { useMastraChatContext } from "../agents/mastra/mastraChatContext.js";
import { ResearchAgentChatView } from "../researchAgent/researchAgentChatView.js";
import { useResearchAgentProps } from "../researchAgent/researchAgentContext.js";
import { ResearchAgentHistoryView } from "../researchAgent/researchAgentHistoryView.js";
import { CallActivityEmptyState } from "./callActivityEmptyState.js";
import type { CallPosition } from "./callNavigationHelpers.js";
import { CallNavigator, type CallNavigatorProps } from "./callNavigator.js";
import {
  parseTranscriptSearchNavState,
  useSortedRecordings,
} from "./gradingHelpers.js";
import {
  GradingTranscriptWithIndex,
  type GradingTranscriptWithIndexHandle,
} from "./gradingTranscriptWithIndex.js";
import { useLiveCoachingPreviewWindow } from "./liveCoachingPreview/liveCoachingPreviewWindow.js";
import { useOpenInteractionGradingReferenceWrapper } from "./openInteractionGradingReferenceAdapter.js";
import { TRANSCRIPT_COLUMN_MAX_WIDTH_CLASS } from "./recordingVideoPanel.js";
import { maybeGetShareSnippetFromUrl } from "./snippetSharing/snippetSharingHelpers.js";
import { TranscriptSearchBar } from "./snippetSharing/transcriptSearchBar.js";
import { useTranscriptSearchNavigation } from "./snippetSharing/useTranscriptSearchNavigation.js";
import { TeammateAccountRecordingsSummary } from "./teammateAccountRecordingsSummary.js";
import { TranscriptVideoPanel } from "./transcriptVideoPanel.js";
import { useCallAudioPosition } from "./useCallAudioPosition.js";
import { useCallNavigationActions } from "./useCallNavigationActions.js";
import { useTranscriptOutlineSync } from "./useTranscriptOutlineSync.js";
import { VoiceAgentCallFeedbackWidget } from "./voiceAgentCallFeedbackWidget.js";

type TranscriptContentView = "transcript" | "summary";

const contentViewOptions: readonly SegmentedControlOption<TranscriptContentView>[] =
  [
    { id: "transcript", label: "Transcript" },
    { id: "summary", label: "Summary" },
  ];

/**
 * Subscribes to the audio clock here so a playback tick re-renders the
 * navigator rather than the whole page.
 */
const AudioSyncedCallNavigator: FC<
  Omit<CallNavigatorProps, "expandedRecordingId" | "audioPosition">
> = (props) => {
  const { recordings } = props;
  const { selectedRecordingId, audioPosition } =
    useCallAudioPosition(recordings);

  return (
    <CallNavigator
      {...props}
      expandedRecordingId={selectedRecordingId}
      audioPosition={audioPosition}
    />
  );
};

/**
 * Page accessible via https://<host>/performance/playbooks/<playbookId>/customer-interactions/<gradingId>/transcript
 * or https://<host>/agents/<teammateAgentId>/customer-interactions/<gradingId>/transcript
 */
export const TrainingTeammatePageOverviewGradingTranscript: FC = () => {
  const { gradingId: gradingIdParam, teammateAgentId } = useParams();
  if (!gradingIdParam) {
    throw new Error("Missing params.");
  }
  const gradingId = asGradingId(gradingIdParam);

  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery({ gradingId });

  // teammate is derived from the grading; the route only carries the grading id.
  const { data: teammate } = useBasicTeammateQuery(
    grading?.teammate_id ?? skipToken,
  );

  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<GradingTranscriptWithIndexHandle>(null);
  const audioPlayer = useContext(AudioPlayerContext);
  const [contentView, setContentView] =
    useState<TranscriptContentView>("transcript");

  // Leaving the transcript, for Summary or another tab, pauses the call where it is
  const nativeAudio = audioPlayer.nativeAudio;
  useEffect(() => {
    if (contentView !== "transcript") {
      return;
    }
    return () => nativeAudio?.pause();
  }, [contentView, nativeAudio]);

  const sortedRecordings = useSortedRecordings(grading?.recordings);

  // Fetch tool activities for all recordings
  const recordingIds = sortedRecordings.map((r) => r.id);
  const { data: toolActivitiesByRecordingId } = useRecordingToolActivitiesQuery(
    recordingIds.length > 0 ? recordingIds : skipToken,
  );

  const {
    data: callOutline,
    isLoading: isCallOutlineLoading,
    isError: isCallOutlineError,
  } = usePlaybookGradingCallOutlineQuery({ gradingId });

  const { callPosition, scrollToCallPosition } = useTranscriptOutlineSync({
    transcriptContainerRef,
    sortedRecordings,
  });
  const { selectCall, selectSegment } = useCallNavigationActions({
    recordings: sortedRecordings,
    onCallPositionRequest: scrollToCallPosition,
  });

  // Show the templated summary in the dedicated Summary view when available.
  const recordingSummary =
    grading?.assessment?.templated_summary?.trim() || null;

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [didShowHistory, setDidShowHistory] = useState(false);
  const handleHistoryOpenChange = (isOpen: boolean) => {
    if (isOpen) setDidShowHistory(true);
    setIsHistoryOpen(isOpen);
  };

  // Ask Alpharun's own provider drives the conversation; this page only
  // decides where it renders. Undefined for a company that doesn't get it.
  const researchAgent = useResearchAgentProps();
  const chat = useMastraChatContext();

  // The provider owns the expanded state, so the docked panel behind this
  // dialog yields the conversation while the chat is open here.
  const isChatExpanded = researchAgent?.isEmbeddedChatOpen ?? false;
  const onEmbeddedChatOpenChange = researchAgent?.onEmbeddedChatOpenChange;
  useEffect(
    () => () => onEmbeddedChatOpenChange?.(false),
    [onEmbeddedChatOpenChange],
  );
  const isOwnThread =
    researchAgent?.threadResourceId === researchAgent?.resourceId;

  // A quote from this call scrolls the transcript here instead of opening the
  // interaction in a new tab, which would only re-open the page it is on.
  const onQuoteFocus = useCallback((quote: string) => {
    setContentView("transcript");
    requestAnimationFrame(() =>
      transcriptRef.current?.focusQuoteText({ quote }),
    );
  }, []);
  const gradingReferenceWrapper = useOpenInteractionGradingReferenceWrapper({
    openGradingId: grading?.id,
    onQuoteFocus,
  });

  const searchNavState = parseTranscriptSearchNavState(useLocation().state);
  const sharedSnippetTarget = maybeGetShareSnippetFromUrl();
  const hasSharedSnippetTarget =
    Boolean(new URLSearchParams(window.location.search).get("quote")) ||
    (sharedSnippetTarget !== undefined &&
      sortedRecordings.some(
        (recording) => recording.id === sharedSnippetTarget.recordingId,
      ));
  const searchNav = useTranscriptSearchNavigation({
    recordings: sortedRecordings,
    navState: searchNavState,
    transcriptRef,
    transcriptContainerRef,
    hasRequestedPosition: audioPlayer.hasRequestedPosition,
  });

  const defaultTranscriptGradingIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // A play or seek made before the transcript arrives wins over the default.
    // The grading isn't marked handled here: the provider keeps the previous
    // grading's request until its sources change, so the next grading still
    // gets its default once that request is cleared.
    if (
      defaultTranscriptGradingIdRef.current === gradingId ||
      searchNavState ||
      hasSharedSnippetTarget ||
      audioPlayer.hasRequestedPosition
    ) {
      return;
    }

    const firstRecording = sortedRecordings[0];
    const firstFragment = firstRecording?.transcript?.fragments[0];
    if (!firstRecording || !firstFragment) {
      return;
    }

    defaultTranscriptGradingIdRef.current = gradingId;
    const position: CallPosition = {
      recordingId: firstRecording.id,
      timestamp: firstFragment.start_time,
      fragmentIndex: 0,
    };
    audioPlayer.seek(position.recordingId, position.timestamp / 1000);
    scrollToCallPosition(position);
  }, [
    audioPlayer,
    gradingId,
    hasSharedSnippetTarget,
    scrollToCallPosition,
    searchNavState,
    sortedRecordings,
  ]);

  const hasLiveCoachingPreview = useIsFeatureFlagEnabled(
    FeatureFlagKey.LiveCoachingPreview,
  );

  const { liveCoachingPreviewWindow, onLiveCoachingPreviewOpen } =
    useLiveCoachingPreviewWindow({
      gradingId,
      sortedRecordings,
    });
  const hasTranscriptActions =
    teammate?.type === TeammateType.AGENT ||
    (searchNavState && searchNav.count > 0) ||
    hasLiveCoachingPreview;
  const onContentViewChange = (nextView: TranscriptContentView) => {
    setContentView(nextView);
    if (nextView === "summary") {
      onEmbeddedChatOpenChange?.(false);
    }
  };

  useEffect(() => {
    if (isChatExpanded) {
      setContentView("transcript");
    }
  }, [isChatExpanded]);

  return (
    <>
      <div className="grid h-full grid-rows-[minmax(0,1fr)_auto_auto] overflow-hidden">
        <div className="relative min-h-0">
          <div className="absolute top-0 left-0 z-20 w-[max(240px,25%)] bg-white px-4 pt-4 pb-3 max-md:w-full">
            <SegmentedControl
              aria-label="Transcript view"
              size={Size.MEDIUM}
              className="w-full max-w-none [&>button]:flex-1"
              options={contentViewOptions}
              value={contentView}
              onChange={onContentViewChange}
            />
          </div>
          <div
            className={twMerge(
              "grid h-full min-h-0 grid-cols-[minmax(240px,25%)_minmax(0,1fr)_auto] max-md:grid-cols-[minmax(0,1fr)] max-md:grid-rows-[minmax(180px,35%)_minmax(0,1fr)_auto]",
              contentView === "summary" && "pointer-events-none invisible",
            )}
            aria-hidden={contentView === "summary"}
            inert={contentView === "summary"}
          >
            <aside
              data-call-scroll-container
              className="min-h-0 overflow-x-hidden overflow-y-auto border-border-light bg-white pt-16 max-md:border-b"
            >
              <div className="px-2 pb-4">
                <AudioSyncedCallNavigator
                  recordings={sortedRecordings}
                  callOutline={callOutline}
                  isOutlineLoading={isCallOutlineLoading}
                  isOutlineError={isCallOutlineError}
                  scrollPosition={callPosition}
                  onCallRequest={selectCall}
                  onSegmentRequest={selectSegment}
                />
              </div>
            </aside>

            <div className="flex min-h-0 flex-col bg-white">
              {hasTranscriptActions ? (
                <div className="flex items-center justify-between gap-4 border-b border-border-light px-4 py-2">
                  {teammate?.type === TeammateType.AGENT ? (
                    <VoiceAgentCallFeedbackWidget
                      gradingId={grading?.id}
                      teammateAgentId={teammateAgentId}
                      existingFeedback={
                        grading?.feedback?.voice_agent_call ?? null
                      }
                    />
                  ) : (
                    <span />
                  )}
                  <div className="flex items-center gap-2">
                    {searchNavState && searchNav.count > 0 ? (
                      <TranscriptSearchBar
                        query={searchNavState.query}
                        count={searchNav.count}
                        currentMatchIndex={searchNav.currentMatchIndex}
                        onPrev={searchNav.prev}
                        onNext={searchNav.next}
                        onClose={searchNav.close}
                      />
                    ) : null}
                    {hasLiveCoachingPreview ? (
                      <Button
                        size={Size.MEDIUM}
                        variant={ButtonVariant.Tertiary}
                        icon={<IconPlayerPlay className="size-4 shrink-0" />}
                        onClick={onLiveCoachingPreviewOpen}
                      >
                        Preview Alpharun Assist
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <TranscriptVideoPanel recordings={sortedRecordings} />
              <TranscriptScrollPane
                scrollContainerRef={transcriptContainerRef}
                className="flex flex-col gap-4 p-4"
              >
                <div
                  data-testid="transcript-content"
                  className={twMerge(
                    "mx-auto w-full",
                    TRANSCRIPT_COLUMN_MAX_WIDTH_CLASS,
                    sortedRecordings.length === 0 && "flex flex-1 flex-col",
                  )}
                >
                  {sortedRecordings.length === 0 ? (
                    <CallActivityEmptyState
                      callActivity={grading?.call_activity}
                      surface="transcript"
                    />
                  ) : null}

                  <GradingTranscriptWithIndex
                    ref={transcriptRef}
                    recordings={sortedRecordings}
                    searchMatches={searchNav.searchMatches}
                    scrollToCallPosition={scrollToCallPosition}
                    toolActivitiesByRecordingId={toolActivitiesByRecordingId}
                  />
                </div>
              </TranscriptScrollPane>
            </div>

            <div className="min-h-0 overflow-hidden">
              {researchAgent && isChatExpanded ? (
                <div className="h-full w-[min(400px,35vw)] shrink-0 border-l border-border-light bg-background max-md:h-56 max-md:w-full max-md:border-t max-md:border-l-0">
                  <ResearchAgentChatView
                    researchAgent={researchAgent}
                    surface="panel"
                    onRequestClose={() =>
                      researchAgent.onEmbeddedChatOpenChange(false)
                    }
                    gradingReferenceWrapper={gradingReferenceWrapper}
                    isHistoryOpen={isHistoryOpen}
                    onHistoryOpenChange={handleHistoryOpenChange}
                    history={
                      didShowHistory ? (
                        <ResearchAgentHistoryView
                          surface="panel"
                          resourceId={researchAgent.resourceId}
                          selectedThreadId={researchAgent.threadId}
                          onThreadSelect={(thread) => {
                            researchAgent.onThreadSelect(thread);
                            setIsHistoryOpen(false);
                          }}
                          onNewThreadSelect={() => {
                            researchAgent.onNewThreadSelect();
                            setIsHistoryOpen(false);
                          }}
                        />
                      ) : undefined
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          {contentView === "summary" ? (
            <section
              className="absolute inset-0 overflow-y-auto bg-white pt-16"
              aria-label="Call summary"
            >
              {isGradingLoading ? (
                <div className="mx-auto max-w-3xl px-6 py-10">
                  <DefaultSkeleton />
                </div>
              ) : recordingSummary ? (
                <div className="mx-auto w-full max-w-3xl px-6 pt-5">
                  <TeammateAccountRecordingsSummary
                    summary={recordingSummary}
                  />
                </div>
              ) : (
                <div className="mx-auto mt-28 flex max-w-md flex-col items-center px-6 text-center">
                  <IconFileText aria-hidden className="size-9 text-slate-300" />
                  <h3 className="mt-4 text-base font-semibold text-foreground">
                    No summary available
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    A summary will appear here after this interaction has been
                    analyzed.
                  </p>
                </div>
              )}
            </section>
          ) : null}
        </div>

        <div className="col-span-full">
          {researchAgent && !isChatExpanded ? (
            <div className="flex items-end gap-2 border-t border-border-light bg-background px-4 py-2.5">
              <ChatComposer
                className="flex-auto"
                placeholder="Ask anything about this call..."
                value={researchAgent.composerDraft}
                onValueChange={researchAgent.onComposerDraftChange}
                isRunning={chat.isRunning}
                // A thread someone else shared is view-only; expanding the
                // chat shows the notice that says so.
                isDisabled={!isOwnThread}
                onSend={(message) => {
                  setContentView("transcript");
                  chat.sendMessage(message);
                  researchAgent.onEmbeddedChatOpenChange(true);
                }}
              />

              <AriaButton
                className="-mr-1 size-10 p-2.5"
                onPress={() => {
                  setContentView("transcript");
                  researchAgent.onEmbeddedChatOpenChange(true);
                }}
              >
                <IconLayoutSidebarRightExpand className="size-5 shrink-0 text-muted-foreground hover:text-gray-600" />
                <VisuallyHidden>Expand research assistant</VisuallyHidden>
              </AriaButton>
            </div>
          ) : null}
        </div>

        {contentView === "transcript" ? (
          <div className="col-span-full">
            <AudioPlayer />
          </div>
        ) : null}
      </div>

      {liveCoachingPreviewWindow}
    </>
  );
};
